import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import Pagination from '../../Tools/Pagination';
import TranferCaseaddmodal from "./TransferCaseModal/TranferCaseaddmodal";
import AddTransferCaseModal from './TransferCaseModal/AddTransferCaseModal';
import { format } from 'date-fns';

// DateTimeFormatFetcher – Fetch date/time format from system settings
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date and time formats:', err);
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const TransferCaseTab = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedTransferCase, setSelectedTransferCase] = useState(null);

  const [transferCases, setTransferCases] = useState({
    all: [],
    transfer_case_from: [],
    transfer_case_to: [],
    pending_case_approval: [],
    hold_case: [],
  });

  const [filteredTransferCases, setFilteredTransferCases] = useState({
    all: [],
    transfer_case_from: [],
    transfer_case_to: [],
    pending_case_approval: [],
    hold_case: [],
  });

  const [page, setPage] = useState({
    all: 1,
    transfer_case_from: 1,
    transfer_case_to: 1,
    pending_case_approval: 1,
    hold_cases: 1,
  });

  const [roleId, setRoleId] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [medicalCenterId, setMedicalCenterId] = useState(null);
  const [medicalCenterName, setMedicalCenterName] = useState(null);
  const [userId, setUserId] = useState(null);

  const [filterForm, setFilterForm] = useState({
    all: { name: '' },
    transfer_case_from: { name: '' },
    transfer_case_to: { name: '' },
    pending_case_approval: { name: '' },
    hold_cases: { name: '' },
  });

  const [locationMap, setLocationMap] = useState({
    All: 'https://www.google.com/maps?q=Riyadh, Saudi Arabia&output=embed',
  });

  const [loading, setLoading] = useState(true); // Controls inline table spinners
  const location = useLocation();
  const navigate = useNavigate();
  const limit = 10;
  const { settings, settingsRef, updateSetting, handleColorTheme } = useSettings();

  // Format date based on system settings
  const formatCaseDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
      const timeFormat = sessionStorage.getItem('time_format') || '24';
      const dateFormatMap = {
        'DD/MM/YYYY': 'dd/MM/yyyy',
        'MM/DD/YYYY': 'MM/dd/yyyy',
        'YYYY-MM-DD': 'yyyy-MM-dd',
      };
      const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
      const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

      let utcDate;
      if (dateString.includes('T')) {
        utcDate = new Date(dateString);
      } else {
        utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
      }

      const utcTimestamp = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );
      return format(utcTimestamp, formatString);
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return 'Invalid Date';
    }
  };

  // Load user, role, and medical center info
  useEffect(() => {
    const storedRoleId = sessionStorage.getItem('Role_id');
    const storedRoleName = sessionStorage.getItem('Role_Name');
    const storedUserId = sessionStorage.getItem('user_id');
    const storedMedicalCenterId = sessionStorage.getItem('medical_center_id');
    const storedMedicalCenterName = sessionStorage.getItem('Medical_Center_Name');

    setRoleId(storedRoleId);
    setRoleName(storedRoleName);
    setUserId(storedUserId);
    setMedicalCenterId(storedMedicalCenterId);
    setMedicalCenterName(storedMedicalCenterName || 'All');

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openModal') === 'true') {
      setModalMode('add');
      setShowModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  // Fetch transfer cases when role & user are available
  useEffect(() => {
    if (roleId && userId) {
      fetchTransferCases();
    }
  }, [roleId, userId]);

  // Apply filters whenever data or filter changes
  useEffect(() => {
    const filterCases = (cases, filter) =>
      Array.isArray(cases)
        ? cases.filter((c) =>
          filter.name
            ? c.case_name?.toLowerCase().includes(filter.name.toLowerCase())
            : true
        )
        : [];

    setFilteredTransferCases({
      all: filterCases(transferCases.all, filterForm.all),
      transfer_case_from: filterCases(transferCases.transfer_case_from, filterForm.transfer_case_from),
      transfer_case_to: filterCases(transferCases.transfer_case_to, filterForm.transfer_case_to),
      pending_case_approval: filterCases(transferCases.pending_case_approval, filterForm.pending_case_approval),
      hold_case: filterCases(transferCases.hold_case, filterForm.hold_cases),
    });
  }, [transferCases, filterForm]);

  // Fetch all transfer cases
  const fetchTransferCases = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/ems/records/${roleId}/transfer_cases/${userId}`,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
        }
      );

      let data;
      const isNurseOrManager = ['Hospital Representative (Nurse)', 'EMS Resource Manager'].includes(roleName);

      if (isNurseOrManager) {
        data = response.data || {
          transfer_case_from: [],
          transfer_case_to: [],
          pending_case_approval: [],
          hold_case: [],
        };
        setTransferCases({
          all: [],
          transfer_case_from: data.transfer_case_from || [],
          transfer_case_to: data.transfer_case_to || [],
          pending_case_approval: data.pending_case_approval || [],
          hold_case: Array.isArray(data.hold_case) ? data.hold_case : [],
        });
      } else {
        data = Array.isArray(response.data) ? response.data : [];
        setTransferCases({
          all: data,
          transfer_case_from: [],
          transfer_case_to: [],
          pending_case_approval: [],
          hold_case: [],
        });
      }
      updateLocationMap(data);
    } catch (error) {
      console.error('Error fetching cases:', error);
      setTransferCases({
        all: [],
        transfer_case_from: [],
        transfer_case_to: [],
        pending_case_approval: [],
        hold_case: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const updateLocationMap = (data) => {
    const updatedMap = { ...locationMap };
    const cases = Array.isArray(data)
      ? data
      : [
        ...(data.transfer_case_from || []),
        ...(data.transfer_case_to || []),
        ...(data.pending_case_approval || []),
        ...(data.hold_case || []),
      ];

    cases.forEach((c) => {
      if (!c?.case_name) return;
      let query =
        c.medical_center?.name ||
        c.from_medical_center?.name ||
        c.nearest_medical_center ||
        c.transfer_from_medical_center ||
        'Riyadh, Saudi Arabia';
      updatedMap[c.case_name] = `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
    });
    setLocationMap(updatedMap);
  };

  // Filter handlers
  const handleFilterChange = (tableKey, e) => {
    const newFilter = { ...filterForm, [tableKey]: { name: e.target.value } };
    setFilterForm(newFilter);
    setPage((prev) => ({ ...prev, [tableKey]: 1 }));
  };

  const clearFilters = (tableKey) => {
    setFilterForm((prev) => ({ ...prev, [tableKey]: { name: '' } }));
    setPage((prev) => ({ ...prev, [tableKey]: 1 }));
  };

  // Modal & actions
  const openModal = (mode, caseItem = null) => {
    setModalMode(mode);
    setSelectedTransferCase(caseItem);
    setShowModal(true);
  };

  const handleTransferDetails = (caseId) => {
    sessionStorage.setItem('currentCaseId', caseId);
    navigate('/ems/transfer-detail');
  };

  const handleDelete = async (caseId) => {
    if (!window.confirm('Are you sure you want to delete this transfer case?')) return;
    try {
      await axiosInstance.delete(`/ems/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` },
      });
      fetchTransferCases();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleResubmit = async (caseId) => {
    if (!window.confirm('Are you sure you want to resubmit this case?')) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/ems/resubmit-the-case/${caseId}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });
      alert(response.data.message || 'Case resubmitted successfully');
      fetchTransferCases();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to resubmit case');
    } finally {
      setLoading(false);
    }
  };

  // Render table with inline loading
  const renderTable = (cases, tableType) => {
    const currentItems = cases.slice((page[tableType] - 1) * limit, page[tableType] * limit);
    const isNurseOrManager = ['Hospital Representative (Nurse)', 'EMS Resource Manager'].includes(roleName);
    const isDoctor = roleName === 'Hospital Representative (Doctor)';

    const showLoadingRow = loading && cases.length === 0;

    if (tableType === 'hold_cases') {
      return (
        <>
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Medical Center</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {showLoadingRow ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : cases.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">No Hold Cases Found</td>
                  </tr>
                ) : (
                  currentItems.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <button className="text-primary border-0 bg-transparent" onClick={() => handleTransferDetails(c.id)}>
                          {c.id || 'N/A'}
                        </button>
                      </td>
                      <td>
                        <button className="text-primary border-0 bg-transparent" onClick={() => handleTransferDetails(c.id)}>
                          {c.case_name || 'N/A'}
                        </button>
                      </td>
                      <td>{c.location?.location_name || 'N/A'}</td>
                      <td>{c.medical_center?.name || `Medical Center ${c.nearest_medical_center || 'N/A'}`}</td>
                      <td>{c.case_status === 2 ? 'Transfer' : c.case_status === 1 ? 'New Case' : 'N/A'}</td>
                      <td>
                        <div className="d-flex gap-2">
                          {!isDoctor && (
                            <>
                              <button className="btn p-0 border-0" onClick={() => handleResubmit(c.id)} disabled={loading}>
                                <i className="ti ti-refresh text-info fs-5"></i>
                              </button>
                              <button className="btn p-0 border-0" onClick={() => handleDelete(c.id)} disabled={loading}>
                                <i className="ti ti-trash text-danger fs-5"></i>
                              </button>
                            </>
                          )}
                          <button className="btn p-0 border-0" onClick={() => handleTransferDetails(c.id)} disabled={loading}>
                            <i className="ti ti-list-details text-success fs-5"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {cases.length > 0 && !loading && (
            <Pagination
              total={cases.length}
              limit={limit}
              page={page[tableType]}
              onPageChange={(newPage) => setPage({ ...page, [tableType]: newPage })}
              disabled={loading}
            />
          )}
        </>
      );
    }

    // Regular tables
    return (
      <>
        <div className="table-responsive rounded-2 ros-table">
          <table className="table text-nowrap customize-table mb-0 align-middle">
            <thead className="text-dark fs-4">
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Transfer From</th>
                <th>Transfer To</th>
                <th>Status</th>
                <th>Case Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {showLoadingRow ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : cases.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">No Transfer Cases Found</td>
                </tr>
              ) : (
                currentItems.map((c, i) => (
                  <tr key={i}>
                    <td>
                      <button className="text-primary border-0 bg-transparent" onClick={() => handleTransferDetails(c.id)}>
                        {c.id || 'N/A'}
                      </button>
                    </td>
                    <td>
                      <button className="text-primary border-0 bg-transparent" onClick={() => handleTransferDetails(c.id)}>
                        {c.case_name || 'N/A'}
                      </button>
                    </td>
                    <td>{c.from_medical_center?.name || `Medical Center ${c.transfer_from_medical_center || 'N/A'}`}</td>
                    <td>{c.medical_center?.name || `Medical Center ${c.nearest_medical_center || 'N/A'}`}</td>
                    <td>{c.doctor_approval_status === 1 ? 'Approved' : c.doctor_approval_status === 0 ? 'Pending' : 'N/A'}</td>
                    <td>{c.created_at ? formatCaseDate(c.created_at) : 'N/A'}</td>
                    <td>
                      <div className="d-flex gap-2">
                        {!isDoctor && (
                          <>
                            <button className="btn p-0 border-0" onClick={() => openModal('edit', c)} disabled={loading}>
                              <i className="ti ti-edit text-warning fs-5"></i>
                            </button>
                            {!isNurseOrManager && (
                              <button className="btn p-0 border-0" onClick={() => handleDelete(c.id)} disabled={loading}>
                                <i className="ti ti-trash text-danger fs-5"></i>
                              </button>
                            )}
                          </>
                        )}
                        <button className="btn p-0 border-0" onClick={() => handleTransferDetails(c.id)} disabled={loading}>
                          <i className="ti ti-list-details text-success fs-5"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {cases.length > 0 && !loading && (
          <Pagination
            total={cases.length}
            limit={limit}
            page={page[tableType]}
            onPageChange={(newPage) => setPage({ ...page, [tableType]: newPage })}
            disabled={loading}
          />
        )}
      </>
    );
  };

  // Filter + Add Button Panel
  const renderFilterAndAdd = (tableKey) => {
    const showAddButton = (tableKey === 'all' || tableKey === 'transfer_case_from') && roleName !== 'Hospital Representative (Doctor)';
    const filterKey = tableKey === 'hold_cases' ? 'hold_cases' : tableKey;

    return (
      <div className="filter-search mt-5">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-md-6 p-2">
              <input
                type="text"
                className="form-control"
                placeholder="Patient Name"
                value={filterForm[filterKey].name}
                onChange={(e) => handleFilterChange(filterKey, e)}
              />
            </div>
            <div className="form-group col-lg-2 p-2">
              <button type="button" className="btn btn-outline-primary btn-rounded" onClick={() => clearFilters(filterKey)}>
                Clear Filter
              </button>
            </div>
          </form>
          {showAddButton && (
            <button className="btn my-2 btn-outline-primary" onClick={() => openModal('add')} disabled={loading}>
              <i className="ti ti-circle-plus fs-4 me-2"></i> Add New Transfer Case
            </button>
          )}
        </div>
      </div>
    );
  };

  const showMultiTables = ['Hospital Representative (Nurse)', 'EMS Resource Manager'].includes(roleName);

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <TopBar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings ref={settingsRef} updateSetting={updateSetting} handleColorTheme={handleColorTheme} settings={settings} />

            {showMultiTables ? (
              <>
                {/* Transfer From */}
                <div className="card mb-4">
                  <div className="px-4 py-3">
                    <h5 className="card-title fw-semibold mb-0">Transfer From ({medicalCenterName})</h5>
                    <p className="card-subtitle">Cases where the center is the source.</p>
                  </div>
                  <div className="card-body">
                    {renderFilterAndAdd('transfer_case_from')}
                    {renderTable(filteredTransferCases.transfer_case_from, 'transfer_case_from')}
                  </div>
                </div>

                {/* Transfer To */}
                <div className="card mb-4">
                  <div className="px-4 py-3">
                    <h5 className="card-title fw-semibold mb-0">Transfer To ({medicalCenterName})</h5>
                    <p className="card-subtitle">Cases where the center is the destination.</p>
                  </div>
                  <div className="card-body">
                    {renderFilterAndAdd('transfer_case_to')}
                    {renderTable(filteredTransferCases.transfer_case_to, 'transfer_case_to')}
                  </div>
                </div>

                {/* Pending for Dr. Approval */}
                <div className="card mb-4">
                  <div className="px-4 py-3">
                    <h5 className="card-title fw-semibold mb-0">Pending for Dr. Approval</h5>
                    <p className="card-subtitle">Cases awaiting doctor approval.</p>
                  </div>
                  <div className="card-body">
                    {renderFilterAndAdd('pending_case_approval')}
                    {renderTable(filteredTransferCases.pending_case_approval, 'pending_case_approval')}
                  </div>
                </div>

                {/* Hold Cases */}
                <div className="card w-100 position-relative overflow-hidden mb-4">
                  <div className="px-4 py-3">
                    <h5 className="card-title fw-semibold mb-0 lh-sm">Hold Cases</h5>
                    <p className="card-subtitle mb-0 mt-1">Cases currently on hold and awaiting further action.</p>
                  </div>
                  <div className="card-body p-4">
                    {renderFilterAndAdd('hold_cases')}
                    {renderTable(filteredTransferCases.hold_case, 'hold_cases')}
                  </div>
                </div>
              </>
            ) : (
              <div className="card mb-4">
                <div className="px-4 py-3">
                  <h5 className="card-title fw-semibold mb-0">Transfer Cases</h5>
                  <p className="card-subtitle">Manage and track all patient transfer cases.</p>
                </div>
                <div className="card-body">
                  {renderFilterAndAdd('all')}
                  {renderTable(filteredTransferCases.all, 'all')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <TranferCaseaddmodal
        show={showModal}
        onHide={() => setShowModal(false)}
        mode={modalMode}
        transferCaseData={selectedTransferCase}
        refreshList={fetchTransferCases}
      />

      <AddTransferCaseModal
        show={showModal && modalMode === 'add'}
        onHide={() => setShowModal(false)}
        refreshList={fetchTransferCases}
      />
    </div>
  );
};

export default TransferCaseTab;